import { Button, Input, TableItem } from 'components/atoms';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useParamId } from 'hooks/useParamId';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCheck, PiTrash } from 'react-icons/pi';
import { useGetContractsByLabel } from 'services/contract';
import { PRODUCT_TYPE } from 'services/contract/types';
import { ILabelForm } from 'services/labels/types';
import {
  usePutLabelDeleteDefaultContract,
  usePutLabelSetDefaultContract,
} from 'services/labels';
import { useGetLicenceConfiguration } from 'services/settings/individual';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

interface IDefaultContractRow {
  id: PRODUCT_TYPE;
  productType: PRODUCT_TYPE;
  productTypeName: string;
}

const productTypes: PRODUCT_TYPE[] = [
  PRODUCT_TYPE.Digital,
  PRODUCT_TYPE.Physical,
  PRODUCT_TYPE.YouTube,
  PRODUCT_TYPE.Merch,
];

const LabelDefaultContracts = () => {
  const { t } = useTranslation();
  const labelId = useParamId('label');
  const licenceConfiguration = useGetLicenceConfiguration();
  const { initialValues }: IFormikContextType<ILabelForm> =
    useFormikContext<ILabelForm>();

  const setDefaultContract = usePutLabelSetDefaultContract();
  const deleteDefaultContract = usePutLabelDeleteDefaultContract();

  const digitalContracts = useGetContractsByLabel(labelId, {
    params: { itemCount: 999, page: 0, ShowOnlyActive: true, ProductType: PRODUCT_TYPE.Digital },
  });
  const physicalContracts = useGetContractsByLabel(labelId, {
    params: { itemCount: 999, page: 0, ShowOnlyActive: true, ProductType: PRODUCT_TYPE.Physical },
  });
  const youtubeContracts = useGetContractsByLabel(labelId, {
    params: { itemCount: 999, page: 0, ShowOnlyActive: true, ProductType: PRODUCT_TYPE.YouTube },
  });
  const merchContracts = useGetContractsByLabel(labelId, {
    params: { itemCount: 999, page: 0, ShowOnlyActive: true, ProductType: PRODUCT_TYPE.Merch },
  });

  const optionsByProductType = useMemo(() => {
    const toOptions = (contracts?: { id: string; title: string }[]) =>
      contracts?.map(contract => ({ value: contract.id, label: contract.title })) ?? [];

    return {
      [PRODUCT_TYPE.Digital]: toOptions(digitalContracts.data?.pages.flat()),
      [PRODUCT_TYPE.Physical]: toOptions(physicalContracts.data?.pages.flat()),
      [PRODUCT_TYPE.YouTube]: toOptions(youtubeContracts.data?.pages.flat()),
      [PRODUCT_TYPE.Merch]: toOptions(merchContracts.data?.pages.flat()),
    } as Partial<Record<PRODUCT_TYPE, { value: string; label: string }[]>>;
  }, [
    digitalContracts.data,
    physicalContracts.data,
    youtubeContracts.data,
    merchContracts.data,
  ]);

  const initialSelectedByType = useMemo(() => {
    const defaults = initialValues.defaultContracts ?? [];
    return productTypes.reduce<Record<number, string>>((acc, type) => {
      const found = defaults.find(
        contract => contract.productType.id === type
      );
      acc[type] = found?.contractId ?? '';
      return acc;
    }, {});
  }, [initialValues.defaultContracts]);

  const [savedByType, setSavedByType] =
    useState<Record<number, string>>(initialSelectedByType);
  const [selectedByType, setSelectedByType] =
    useState<Record<number, string>>(initialSelectedByType);
  const [pendingProductType, setPendingProductType] =
    useState<PRODUCT_TYPE | null>(null);

  useEffect(() => {
    setSavedByType(initialSelectedByType);
    setSelectedByType(initialSelectedByType);
  }, [initialSelectedByType]);

  useEffect(() => {
    const loadedByType: Partial<Record<PRODUCT_TYPE, boolean>> = {
      [PRODUCT_TYPE.Digital]: !digitalContracts.isLoading,
      [PRODUCT_TYPE.Physical]: !physicalContracts.isLoading,
      [PRODUCT_TYPE.YouTube]: !youtubeContracts.isLoading,
      [PRODUCT_TYPE.Merch]: !merchContracts.isLoading,
    };

    setSelectedByType(prev => {
      let changed = false;
      const next = { ...prev };

      productTypes.forEach(type => {
        const selectedContractId = next[type];
        if (
          !selectedContractId ||
          !loadedByType[type] ||
          (optionsByProductType[type] ?? []).some(
            option => option.value === selectedContractId
          )
        ) {
          return;
        }

        next[type] = '';
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [
    optionsByProductType,
    digitalContracts.isLoading,
    physicalContracts.isLoading,
    youtubeContracts.isLoading,
    merchContracts.isLoading,
  ]);

  const rows: IDefaultContractRow[] = useMemo(
    () =>
      productTypes.map(type => ({
        id: type,
        productType: type,
        productTypeName: t(`enum.PRODUCT_TYPE.${PRODUCT_TYPE[type]}`),
      })),
    [t]
  );

  const isLoading =
    digitalContracts.isLoading ||
    physicalContracts.isLoading ||
    youtubeContracts.isLoading ||
    merchContracts.isLoading;

  const onSave = async (productType: PRODUCT_TYPE) => {
    const selectedContractId = selectedByType[productType];
    const savedContractId = savedByType[productType];

    if (!selectedContractId && savedContractId) {
      setPendingProductType(productType);
      try {
        await deleteDefaultContract.mutateAsync(
          {
            labelId,
            contractId: savedContractId,
          },
          {
            onSuccess: () => {
              setSavedByType(prev => ({ ...prev, [productType]: '' }));
              setSelectedByType(prev => ({ ...prev, [productType]: '' }));
            },
            onError: error => onErrorShowAllErrorsToast(error),
          }
        );
      } finally {
        setPendingProductType(null);
      }
      return;
    }

    if (!selectedContractId) {
      return;
    }

    setPendingProductType(productType);
    try {
      await setDefaultContract.mutateAsync(
        {
          labelId,
          productType,
          contractId: selectedContractId,
        },
        {
          onSuccess: () => {
            setSavedByType(prev => ({ ...prev, [productType]: selectedContractId }));
          },
          onError: error => onErrorShowAllErrorsToast(error),
        }
      );
    } finally {
      setPendingProductType(null);
    }
  };

  const onDelete = async (productType: PRODUCT_TYPE) => {
    const contractId = savedByType[productType];
    if (!contractId) {
      return;
    }

    setPendingProductType(productType);
    try {
      await deleteDefaultContract.mutateAsync(
        {
          labelId,
          contractId,
        },
        {
          onSuccess: () => {
            setSavedByType(prev => ({ ...prev, [productType]: '' }));
            setSelectedByType(prev => ({ ...prev, [productType]: '' }));
          },
          onError: error => onErrorShowAllErrorsToast(error),
        }
      );
    } finally {
      setPendingProductType(null);
    }
  };

  if (!licenceConfiguration.data?.clientModuleAccess) {
    return null;
  }

  return (
      <Table
        id='LabelDefaultContracts'
        size='compact'
        data={{ nodes: rows }}
        loading={isLoading}
        columns={[
          {
            label: t('label.defaultContracts.typeColumn'),
            renderCell: ({ productTypeName }) => (
              <TableItem>
                <TableItem.Text text={productTypeName} />
              </TableItem>
            ),
          },
          {
            label: t('label.defaultContracts.contractColumn'),
            renderCell: ({ productType }) => {
              const selectedContractId = selectedByType[productType];
              const contractOptions = optionsByProductType[productType] ?? [];
              const selectedOption = contractOptions.find(
                (option: { value: string; label: string }) =>
                  option.value === selectedContractId
              );
              const hasChanges = selectedByType[productType] !== savedByType[productType];
              const hasSavedValue = !!savedByType[productType];
              const isRowPending = pendingProductType === productType;

              return (
                <TableItem>
                  <div className='flex items-end gap-0 w-full'>
                    <div className='w-full max-w-[500px] flex items-center gap-[6px]'>
                      <>
                      <Input.Select
                        key={`default-contract-${productType}-${selectedContractId || 'empty'}`}
                        name={`default-contract-${productType}`}
                        label={t('input.label.contract')}
                        options={contractOptions}
                        value={selectedOption}
                        isLoading={isLoading}
                        isDisabled={isRowPending}
                        isClearable={false}
                        isSearchable
                        isAsync
                        onChange={option =>
                          setSelectedByType(prev => ({
                            ...prev,
                            [productType]: `${option.value}`,
                          }))
                        }
                      />
                       {hasChanges ? (
                      <Button
                        variant={BUTTON_VARIANT.submit}
                        size='compact'
                        onlyIcon
                        onClick={() => onSave(productType)}
                        isLoading={isRowPending}
                        disabled={
                          isRowPending ||
                          (!selectedByType[productType] && !savedByType[productType])
                        }
                      >
                                                <Button.Description description={t('button.save')} />

                                                <Button.Icon icon={<PiCheck />} />
                      </Button>
                    ) : (
                      <Button
                        variant={BUTTON_VARIANT.remove}
                        size='compact'
                        onlyIcon
                        onClick={() => onDelete(productType)}
                        isLoading={isRowPending}
                        disabled={
                          isRowPending || !hasSavedValue
                        }
                      >
                                                <Button.Description description={t('button.remove')} />

                                                <Button.Icon icon={<PiTrash />} />
                      </Button>
                    )}
                      </>
                    </div>
                   
                  </div>
                </TableItem>
              );
            },
          },
        ]}
      />
  );
};

export default LabelDefaultContracts;
